import {useLayoutEffect} from "react";
import {Navigate, useNavigate} from "react-router-dom";
import {useCabinetData} from "@/hooks/useCabinetData";
import {formatDate} from "@/lib/cabinet";

export function CabinetCancellationConfirmedPage() {
    const navigate = useNavigate();
    const {subscription, paid, isLoading, isError, refetch} = useCabinetData();

    useLayoutEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: "auto"});
    }, []);

    if (isLoading) {
        return (
            <div className="cabinet-state">
                <h2>Проверяем статус подписки</h2>
                <p>Подтверждаем отключение регулярных платежей.</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="cabinet-state">
                <h2>Не удалось загрузить подтверждение</h2>
                <button type="button" className="btn btn-primary" onClick={() => void refetch()}>
                    Повторить
                </button>
            </div>
        );
    }

    if (!paid || !subscription) {
        return <Navigate to="/subscribe/" replace/>;
    }

    if (subscription.cancel_at_period_end !== true) {
        return <Navigate to="/cabinet" replace/>;
    }

    const activeUntil = formatDate(subscription.current_period_end, {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    return (
        <section className="cabinet-cancel-complete-flow" aria-labelledby="cancel-complete-title">
            <article className="cabinet-panel cabinet-cancel-complete-card">
                <div className="cabinet-cancel-complete-visual" aria-hidden="true">
                    <span className="cabinet-cancel-complete-orbit cabinet-cancel-complete-orbit-one"/>
                    <span className="cabinet-cancel-complete-orbit cabinet-cancel-complete-orbit-two"/>
                    <span className="cabinet-cancel-complete-mark">
                        <svg viewBox="0 0 48 48">
                            <path d="m14 25 7 7 14-16"/>
                        </svg>
                    </span>
                </div>

                <div className="cabinet-cancel-complete-copy">
                    <span className="cabinet-cancel-eyebrow">Автопродление отключено</span>
                    <h2 id="cancel-complete-title">Регулярные платежи отменены</h2>
                    <p>
                        Подписка продолжит действовать {activeUntil ? (
                            <>до <time dateTime={subscription.current_period_end ?? undefined}>{activeUntil}</time></>
                        ) : "до конца оплаченного периода"}. До этого момента все функции
                        Лидхолда останутся доступны в обычном режиме.
                    </p>
                </div>

                <div className="cabinet-cancel-complete-status" aria-label="Статус подписки">
                    <div>
                        <span>Следующее списание</span>
                        <strong className="is-cancelled">
                            <i aria-hidden="true"/>
                            Отменено
                        </strong>
                        <small>Новых списаний по этой подписке не будет.</small>
                    </div>
                    <div>
                        <span>Доступ к Лидхолду</span>
                        <strong>
                            {activeUntil ? (
                                <>До <time dateTime={subscription.current_period_end ?? undefined}>{activeUntil}</time></>
                            ) : "До конца периода"}
                        </strong>
                        <small>Продолжайте пользоваться сервисом без ограничений.</small>
                    </div>
                </div>

                <div className="cabinet-cancel-complete-note">
                    <span aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                            <path d="M12 3 5 6v5c0 4.6 2.8 8.2 7 10 4.2-1.8 7-5.4 7-10V6Z"/>
                            <path d="m9 12 2 2 4-5"/>
                        </svg>
                    </span>
                    <div>
                        <strong>Ваши данные и настройки сохранятся</strong>
                        <p>Кампании, диалоги и рабочие материалы останутся доступны до завершения подписки.</p>
                    </div>
                </div>

                <button
                    type="button"
                    className="btn btn-primary cabinet-cancel-complete-action"
                    onClick={() => navigate("/cabinet")}
                >
                    Вернуться в личный кабинет
                </button>
            </article>
        </section>
    );
}
